import { useEffect } from "react";

import { App, Col, Row, Spin, theme, Typography } from "antd";
import { Link, Navigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet-async";
import { HeartOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import _ from "lodash";

import {
  useGetDiscussionByIdQuery,
  useLazyIncrementDiscussionViewsQuery,
  useLazyGetTopicsQuery,
  useLikeDiscussionMutation,
} from "../../redux/service.js";
import { setBreadcrumbs } from "../../redux/utils/reducer.js";
import PrimaryButton from "../../components/PrimaryButton.jsx";
import Comments from "./components/Comments.jsx";
import DiscussionTools from "./components/DiscussionTools.jsx";
import TextElement from "@/components_v2/ContentElements/TextElement";
import { TextType } from "@/components_v2/ContentElements/types";
import UserAvatar from "../../domains/users/components/UserAvatar.tsx";
import HeartIcon from "@/assets/icons/heart-icon.svg?react";
import CommentsIcon from "@/assets/icons/comments.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.js";
import LinkedInIcon from "@/assets/icons/linkedin.svg?react";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";
import LikedByPreview from "@/domains/community/components/LikedByPreview.tsx";

const { Title, Text } = Typography;

const MetaTags = ({ discussion }) => (
  <Helmet>
    <title>{`PRME Commons - ${discussion.title}`}</title>
    <meta name="og:title" content={`PRME Commons - ${discussion.title}`} />
    <meta name="og:url" content={window.location.href} />
    <meta name="og:article" content="article" />
    <meta name="og:description" content={_.truncate(discussion.content)} />
  </Helmet>
);

MetaTags.propTypes = {
  discussion: PropTypes.object.isRequired,
};

const getCreationDate = (createdAt) => {
  if (DateTime.fromISO(createdAt).diff(DateTime.now(), "days").days > 2) {
    return DateTime.fromISO(createdAt).toRelative();
  } else {
    return DateTime.fromISO(createdAt).toFormat("dd MMM yyyy");
  }
};

const SingleDiscussion = () => {
  const { discussionId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  const { notification } = App.useApp();

  const {
    data: discussion,
    isSuccess,
    isError,
    isLoading,
  } = useGetDiscussionByIdQuery(discussionId);
  const [incrementDiscussionViews] = useLazyIncrementDiscussionViewsQuery();
  const [getTopics, topicsResponse] = useLazyGetTopicsQuery();
  const [likeDiscussion] = useLikeDiscussionMutation();
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    if (
      isSuccess &&
      currentUser &&
      discussion.createdBy.id !== currentUser.id
    ) {
      setTimeout(() => {
        incrementDiscussionViews(discussion.id);
      }, 15000);
    }
  }, [isSuccess, currentUser]);

  useEffect(() => {
    if (isSuccess) {
      getTopics({ ids: discussion.topics, pagination: "off" });
      dispatch(
        setBreadcrumbs([
          {
            title: t("community.labels.community"),
            to: "/community",
          },
          {
            title: topicsResponse.isSuccess
              ? topicsResponse.data
                  .filter((i) => discussion.topics.includes(i.id))
                  .map((i) => i.title)
                  .join(", ")
              : null,
          },
          {
            title: discussion.title,
          },
        ]),
      );
    }

    return () => dispatch(setBreadcrumbs(null));
  }, [topicsResponse.isSuccess, isSuccess]);

  const handleLinkedInSharing = () => {
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}`,
      "_blank",
    );
  };

  const handleDiscussionLike = () => {
    likeDiscussion({
      id: discussion.id,
      isLiked: !discussion.isLikedSelf,
    })
      .unwrap()
      .catch((error) =>
        notification.error({ message: error.data.errors[0].detail }),
      );
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return <Navigate to="/community" />;
  }

  const normalizedDiscussionContent = discussion.content
    .replaceAll("&nbsp;", " ")
    .replaceAll("\u00A0", " ");

  return (
    isSuccess &&
    currentUser && (
      <div className="mb-5 discussion-container">
        <MetaTags discussion={discussion} />
        <div style={{ borderBottom: "1px solid rgba(1, 1, 46, 0.134)" }}>
          <Row className="pb-3">
            <Col className="ps-2 pe-3" span={23}>
              <div style={{ display: "flex" }}>
                <div className="me-3">
                  <UserAvatar user={discussion.createdBy} />
                </div>
                <div>
                  <Text
                    style={{
                      display: "block",
                      color: "rgba(139, 141, 152, 1)",
                    }}
                  >
                    {t("postedOn")} {getCreationDate(discussion.createdAt)}
                  </Text>
                  <Text
                    style={{
                      display: "block",
                      color: "rgba(139, 141, 152, 1)",
                    }}
                  >
                    {t("by")}{" "}
                    <Link to={`/users/${discussion.createdBy.id}`}>
                      {discussion.createdBy.fullName}
                    </Link>
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={1}>
              <DiscussionTools
                discussion={discussion}
                iconProps={{
                  style: {
                    fontSize: "30px",
                    fontWeight: "bold",
                    transform: "rotate(90deg)",
                    color: "rgba(74, 105, 156, 1)",
                  },
                }}
              />
            </Col>
          </Row>
        </div>
        <Row className="mt-4">
          <Col span={20} className="ps-3 pe-5">
            <Title
              className="mb-2"
              level={3}
              style={{ color: token.colorTextThird, fontWeight: "bold" }}
            >
              {discussion.title}
            </Title>
            <div className="mt-4 mb-5">
              <TextElement
                value={normalizedDiscussionContent}
                type={TextType.RICH_TEXT}
              />
            </div>
            <div className="mt-5">
              {discussion.attachedBlocks.length > 0 && (
                <div className="mt-3 mb-4">
                  {discussion.attachedBlocks.map((block) => (
                    <div
                      className="mt-3 mb-4"
                      style={{
                        background: "rgba(0,0,0,0.02)",
                        padding: "10px",
                      }}
                    >
                      <Link to={`/evidence/${block.id}`}>
                        <Title
                          level={5}
                          style={{ color: token.colorTextThird }}
                        >
                          {
                            block.fieldValues.find(
                              (value) =>
                                value.field === block.fieldsMapping.title,
                            )?.value
                          }
                        </Title>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              {discussion.surveyViews.length > 0 && (
                <div className="mt-3 mb-4">
                  {discussion.surveyViews.map((i) => (
                    <div
                      className="mt-3 mb-4"
                      style={{
                        background: "rgba(0,0,0,0.02)",
                        padding: "10px",
                      }}
                    >
                      <Link
                        to={`/reports/views/${i.id}`}
                        style={{ textDecoration: "underline" }}
                      >
                        <Title
                          level={5}
                          style={{ color: token.colorTextThird }}
                        >
                          {i.title}
                        </Title>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: "50px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              {discussion.createdBy.id !== currentUser.id && (
                <Text
                  style={{
                    fontSize: "17px",
                    color: "rgba(74, 105, 156, 1)",
                    cursor: "pointer",
                  }}
                  onClick={handleDiscussionLike}
                >
                  {discussion.isLikedSelf ? (
                    <BaseSvgRenderer
                      component={HeartIcon}
                      width="14"
                      height="14"
                    />
                  ) : (
                    <HeartOutlined style={{ color: "rgba(74, 105, 156, 1)" }} />
                  )}
                  <span className="ms-2">{t("likeDiscussion")}</span>
                </Text>
              )}
              <LikedByPreview
                entity="discussion"
                id={discussion.id}
                likesCount={discussion.likesCount}
              />
            </div>
          </Col>
          <Col span={4}>
            {[
              {
                icon: (
                  <BaseSvgRenderer
                    component={CommentsIcon}
                    width="14"
                    height="14"
                    style={{
                      filter:
                        "invert(59%) sepia(10%) saturate(275%) hue-rotate(193deg) brightness(93%) contrast(88%)",
                    }}
                  />
                ),
                text: t("commentsCount", {
                  count: discussion.commentsCount,
                  plural: discussion.commentsCount === 1 ? "" : "s",
                }),
              },
            ].map((i) => (
              <Text
                key={i.text}
                className="mb-2"
                style={{
                  display: "block",
                  color: "rgba(139, 141, 152, 1)",
                  fontSize: "15px",
                }}
              >
                <span className="me-2">{i.icon}</span>
                {i.text}
              </Text>
            ))}
            <div className="mt-3">
              <PrimaryButton type="secondary" onClick={handleLinkedInSharing}>
                <Text level={5} style={{ color: "white" }}>
                  <span className="me-1">
                    <BaseSvgRenderer component={LinkedInIcon} />
                  </span>{" "}
                  {t("shareDiscussion")}
                </Text>
              </PrimaryButton>
            </div>
            <div className="mt-4">
              <Title level={5} style={{ color: token.colorTextThird }}>
                {t("subjectTags")}
              </Title>
              {discussion.subjects.map((subject) => (
                <Title
                  level={5}
                  key={subject}
                  className="mt-2"
                  style={{ color: "rgba(0, 37, 158, 0.797)", fontSize: "14px" }}
                >
                  {subject.name}
                </Title>
              ))}
            </div>
          </Col>
        </Row>
        <Comments discussion={discussion} />
      </div>
    )
  );
};

export default SingleDiscussion;
